/* eslint-disable @typescript-eslint/explicit-member-accessibility */
/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";
import { Autobind } from "@Internal/Utils";

import { CustomError } from "@Internal/Models";
import { EventBus } from "App/IOC";

export interface SetupGatewayRow {
    Id: number;
    Code: string;
    Name: string;
    Auto: boolean;
    Username: string;
    GatewayId: number;
    GatewayName: string;
    GatewayImage: string;
    UserId: number;
    UserName: string;
    GroupId: number;
    SubListings: number;
    LastExport: string;
    CreatedAt: string;
    CoordinatesType: string;
}

async function ProcessException( exception: any ): Promise<CustomError> {
    if( exception.name === "NotAuthenticated" )
        EventBus.Publish( "InvalidAuthorization" );

    if( exception.Id !== undefined )
        return new CustomError( exception.Id, exception.Message );

    return new CustomError( "SetupGatewayService", String( exception ) );
}

/* Reads vary in casing/nesting depending on what the app /console/setup-gateway/* endpoints return,
   so every field is pulled through a tolerant fallback. Tighten once the real payload is confirmed
   on staging. */
function pick( source: any, ...keys: string[] ): any {
    if( source === undefined || source === null )
        return undefined;

    for( const key of keys ) {
        if( source[ key ] !== undefined && source[ key ] !== null )
            return source[ key ];
    }

    return undefined;
}

function normalizeRow( raw: any ): SetupGatewayRow {
    const gateway = pick( raw, "gateway", "Gateway" ) || {};
    const user = pick( raw, "user", "User" ) || {};

    const firstName = pick( user, "firstname", "firstName", "FirstName" ) || "";
    const lastName = pick( user, "lastname", "lastName", "LastName" ) || "";
    const userLabel = `${ firstName } ${ lastName }`.trim()
        || pick( user, "email", "Email" )
        || pick( raw, "created_by", "createdBy", "CreatedBy" )
        || String( pick( raw, "user_id", "userId", "UserId" ) || "" );

    return {
        Id: Number( pick( raw, "id", "Id" ) ),
        Code: String( pick( raw, "code", "Code" ) || "" ),
        Name: String( pick( raw, "name", "Name" ) || "" ),
        Auto: Boolean( pick( raw, "auto", "Auto" ) ),
        Username: String( pick( raw, "username", "Username" ) || "" ),
        GatewayId: Number( pick( gateway, "id", "Id" )
            || pick( raw, "gatewayId", "gateway_id", "GatewayId" ) ),
        GatewayName: String( pick( gateway, "name", "Name" ) || "" ),
        GatewayImage: String( pick( gateway, "image", "Image" ) || "" ),
        UserId: Number( pick( user, "id", "Id" )
            || pick( raw, "user_id", "userId", "UserId" ) ),
        UserName: userLabel,
        GroupId: Number( pick( raw, "groupId", "group_id", "GroupId" ) || 0 ),
        SubListings: Number(
            pick( raw, "sub_listings", "subListings", "SubListings" ) || 0
        ),
        LastExport: String( pick( raw, "last_export", "lastExport", "LastExport" ) || "" ),
        CreatedAt: String( pick( raw, "created_at", "createdAt", "CreatedAt" ) || "" ),
        /* Anciens comptes : coordinates_type null côté backend → laisser vide
           (le défaut sale_point n'est appliqué qu'à la création d'un portail). */
        CoordinatesType: String(
            pick( raw, "coordinates_type", "coordinatesType", "CoordinatesType" ) || ""
        )
    };
}

/* Per-user gateway setups (a customer user activating a portal — e.g. Amanda — with its own
   credentials). create is scoped by the target user_id; list/get/update/delete are scoped by the
   group the user belongs to (groupId, carried on the query string). */
export default class SetupGatewayService {
    private service: Feathers.Service<any>;

    constructor( service: Feathers.Service<any> ) {
        this.service = service;
    }

    @Autobind
    async ListByGroup(
        groupId: number,
        page: number = 1,
        limit: number = 100,
        keyword?: string
    ): Promise<[ SetupGatewayRow[], CustomError | undefined ]> {
        try {
            const query: any = { GroupId: groupId, Page: page, Limit: limit };
            if( keyword !== undefined && keyword !== "" )
                query.Name = keyword;

            const results = await this.service.find( { query } );

            /* The app API answers HAL-style ({ _embedded: { items } }, total) and that shape
               passes through the Console proxy untouched (SetupGateway skips serialization).
               Earlier shapes (Items / items / data) kept as fallbacks. */
            const embedded = results._embedded || results.Embedded || {};
            const items =
                results.Items || results.items || embedded.items || results.data || [];
            const rows = ( items as any[] ).map( normalizeRow );

            return [ rows, undefined ];

        } catch( error ) {
            return [ [], await ProcessException( error ) ];
        }
    }

    @Autobind
    async Get(
        id: number,
        groupId: number
    ): Promise<[ SetupGatewayRow | undefined, CustomError | undefined ]> {
        try {
            const record = await this.service.get( id, { query: { GroupId: groupId } } );

            return [ normalizeRow( record ), undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Create(
        payload: {
            UserId: number;
            GatewayId: number;
            Code?: string;
            Name?: string;
            Username?: string;
            Password?: string;
            Auto?: boolean;
            CoordinatesType?: string;
        }
    ): Promise<[ any | undefined, CustomError | undefined ]> {
        try {
            const record = await this.service.create( {
                user_id: payload.UserId,
                gatewayId: payload.GatewayId,
                code: payload.Code || "",
                name: payload.Name || "",
                username: payload.Username || "",
                password: payload.Password || "",
                auto: payload.Auto === true,
                coordinatesType: payload.CoordinatesType || "sale_point"
            } );

            return [ record, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Update(
        id: number,
        groupId: number,
        payload: {
            GatewayId?: number;
            Name?: string;
            Code?: string;
            Username?: string;
            Password?: string;
            Auto?: boolean;
            CoordinatesType?: string;
        }
    ): Promise<[ any | undefined, CustomError | undefined ]> {
        try {
            const data: any = {};
            if( payload.GatewayId !== undefined ) data.gatewayId = payload.GatewayId;
            if( payload.Name !== undefined ) data.name = payload.Name;
            if( payload.Code !== undefined ) data.code = payload.Code;
            if( payload.Username !== undefined ) data.username = payload.Username;
            if( payload.Password !== undefined && payload.Password !== "" )
                data.password = payload.Password;
            if( payload.Auto !== undefined ) data.auto = payload.Auto === true;
            /* Ancien portail non paramétré : ne pas pousser une valeur vide
               (enum backend = agent / sale_point). */
            if( payload.CoordinatesType !== undefined && payload.CoordinatesType !== "" )
                data.coordinatesType = payload.CoordinatesType;

            const record = await this.service.update( id, data, { query: { GroupId: groupId } } );

            return [ record, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }

    @Autobind
    async Remove(
        id: number,
        groupId: number
    ): Promise<[ any | undefined, CustomError | undefined ]> {
        try {
            const record = await this.service.remove( id, { query: { GroupId: groupId } } );

            return [ record, undefined ];

        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }
}
